import { apiClient } from "./client";

export type UserStatus = "ACTIVE" | "INVITED" | "SUSPENDED" | "ARCHIVED";

export type AdminUser = {
  id: string;
  email: string;
  full_name: string | null;
  tenant_id: string | null;
  status: UserStatus;
  is_god: boolean;
  mfa_enabled: boolean;
  practitioner_id: string | null;
  created_at: string;
  updated_at: string;
  has_password: boolean;
  has_pending_invite: boolean;
  /** Only set by create / resend-invite: false means SMTP refused or is unconfigured. */
  invite_email_sent?: boolean | null;
};

export type AdminUserPage = {
  items: AdminUser[];
  total: number;
  limit: number;
  offset: number;
};

export type CreateUserPayload = {
  email: string;
  full_name: string;
  role_code: string;
  org_id?: string | null;
  /** Grants the role on several organisations at once; takes precedence over org_id. */
  org_ids?: string[];
  practitioner_id?: string | null;
};

export type UpdateUserPayload = {
  full_name?: string | null;
  status?: "ACTIVE" | "SUSPENDED";
};

export async function listUsers(params: {
  q?: string;
  status?: UserStatus;
  limit?: number;
  offset?: number;
} = {}): Promise<AdminUserPage> {
  const { data } = await apiClient.get<AdminUserPage>("/api/v1/admin/users", { params });
  return data;
}

export async function createUser(payload: CreateUserPayload): Promise<AdminUser> {
  const { data } = await apiClient.post<AdminUser>("/api/v1/admin/users", payload);
  return data;
}

export async function updateUser(id: string, payload: UpdateUserPayload): Promise<AdminUser> {
  const { data } = await apiClient.patch<AdminUser>(`/api/v1/admin/users/${id}`, payload);
  return data;
}

export async function resendInvite(id: string): Promise<AdminUser> {
  const { data } = await apiClient.post<AdminUser>(
    `/api/v1/admin/users/${id}/resend-invite`,
  );
  return data;
}

export type RoleScope = "GOD" | "TENANT" | "ORG";

export type UserRoleAssignment = {
  id: string;
  role_code: string;
  role_label: string;
  scope: RoleScope;
  scope_id: string | null;
  scope_label: string | null;
};

export async function listUserRoles(userId: string): Promise<UserRoleAssignment[]> {
  const { data } = await apiClient.get<UserRoleAssignment[]>(
    `/api/v1/admin/users/${userId}/roles`,
  );
  return data;
}

export async function addUserRole(
  userId: string,
  payload: { role_code: string; org_id?: string | null },
): Promise<UserRoleAssignment> {
  const { data } = await apiClient.post<UserRoleAssignment>(
    `/api/v1/admin/users/${userId}/roles`,
    payload,
  );
  return data;
}

export async function updateUserRole(
  userId: string,
  assignmentId: string,
  payload: { role_code: string; org_id?: string | null },
): Promise<UserRoleAssignment> {
  const { data } = await apiClient.patch<UserRoleAssignment>(
    `/api/v1/admin/users/${userId}/roles/${assignmentId}`,
    payload,
  );
  return data;
}

export async function removeUserRole(userId: string, assignmentId: string): Promise<void> {
  await apiClient.delete(`/api/v1/admin/users/${userId}/roles/${assignmentId}`);
}

export async function setupPasswordWithToken(
  token: string,
  newPassword: string,
): Promise<void> {
  await apiClient.post("/api/v1/auth/setup-password", {
    token,
    new_password: newPassword,
  });
}
